<template>
  <ion-app>
    <ion-router-outlet />
  </ion-app>
</template>

<script lang="ts">
import { IonApp, IonRouterOutlet, alertController } from '@ionic/vue';
import { defineComponent, onMounted, onUnmounted, watch, watchEffect, ref } from 'vue';
import { supabase } from './supabase';
import { useRouter } from 'vue-router';
import { Capacitor } from '@capacitor/core';
import { App } from '@capacitor/app';
import { useUserStore } from '@/store/user';
import { realtimeService } from '@/services/realtimeService';
import { useNotifications } from '@/store/notifications';
import TokenHandler from '@/components/TokenHandler.vue';
import { authMiddleware } from '@/services/authMiddleware';
//import { initializeCreditChecks, processCustomerCreditCheck } from '@/services/creditCheckInit';
import '@ionic/vue/css/core.css';
import '@ionic/vue/css/normalize.css';
import '@ionic/vue/css/structure.css';
import '@ionic/vue/css/typography.css';
import './theme/icons.css';
import { FCM } from '@capacitor-community/fcm';
import { appUpdateService } from './services/appUpdateService';

// Add interfaces
interface RouteComponent {
  fetchCustomers?: () => Promise<void>;
}

interface AppStateChange {
  isActive: boolean;
}

interface SavedState {
  route: any;
  timestamp: number;
}

// CRITICAL: Capture tokens from URL as early as possible
// This runs immediately at module load time, before any Vue components
(function captureEarlyTokens() {
  try {
    console.log('🔄 Early token capture - checking URL');
    
    // Use the auth middleware for token capture
    if (authMiddleware.captureRecoveryToken()) {
      console.log('✅ Successfully captured tokens at app initialization');
      
      // Ensure we're on the reset password page if we need to be
      const isResetPage = window.location.pathname.includes('/reset-password');
      if (!isResetPage && authMiddleware.isInPasswordResetFlow()) {
        console.log('🔄 Redirecting to reset-password page with captured token');
        window.location.href = `${window.location.origin}/reset-password`;
        // Stop execution - we're redirecting
        return;
      }
    }
  } catch (e) {
    console.error('Error in early token capture:', e);
  }
})();

export default defineComponent({
  name: 'App',
  components: {
    IonApp,
    IonRouterOutlet,
    TokenHandler
  },
  data() {
    return {
      currentAppVersion: String,
      appStoreAppVersion: String
    }
  },
  methods: {
   
  },
  setup() {
    // Initialize router and stores
    const router = useRouter();
    const userStore = useUserStore();
    const notificationsStore = useNotifications();
    let notificationSubscription: any = null;

    // Force light mode for all modals by adding class to document root
    const forceLightMode = () => {
      document.documentElement.classList.add('force-light-mode');
      // Also add to body for good measure
      document.body.classList.add('force-light-mode');
    };

    // Fix toggle buttons visibility
    const fixToggleButtons = () => {
      // Apply immediately and then also after a delay to catch elements that load later
      const applyFixes = () => {
        const toggles = document.querySelectorAll('ion-toggle');
        toggles.forEach(toggle => {
          if (toggle instanceof HTMLElement) {
            toggle.style.opacity = '1';
            toggle.style.visibility = 'visible';
            toggle.style.display = 'block';
            toggle.style.border = 'none';
            
            // Apply custom styles through shadow DOM
            const styleEl = document.createElement('style');
            styleEl.textContent = `
              :host {
                opacity: 1 !important;
                visibility: visible !important;
              }
              .toggle-inner {
                box-shadow: inset 0 1px 3px rgba(0, 0, 0, 0.2) !important;
              }
              .toggle-handle {
                box-shadow: 0 3px 6px rgba(0, 0, 0, 0.2) !important;
                transition: all 0.3s ease !important;
              }
            `;
            
            // Attempt to inject styles into shadow DOM
            if (toggle.shadowRoot) {
              toggle.shadowRoot.appendChild(styleEl);
            }
          }
        });
      };
      
      // Apply immediately
      applyFixes();
      
      // Apply again after a delay to catch dynamically added elements
      setTimeout(applyFixes, 300);
      setTimeout(applyFixes, 1000);
      
      // Set up a mutation observer to handle dynamically added toggles
      const observer = new MutationObserver((mutations) => {
        mutations.forEach(mutation => {
          if (mutation.type === 'childList') {
            applyFixes();
          }
        });
      });
      
      // Start observing
      observer.observe(document.body, {
        childList: true,
        subtree: true
      });
    };

    // Check the auto initialization status
    FCM.isAutoInitEnabled().then(r => {
      console.log('Auto init is ' + (r.enabled ? 'enabled' : 'disabled'));
    });

    const refreshData = async () => {
      try {
        // Refresh user data
        const currentUser = userStore.user.value;
        if (currentUser?.id) {
          const { data, error } = await supabase
            .from('profiles')
            .select('*')
            .eq('id', currentUser.id)
            .single();
            
          if (!error && data) {
            userStore.setUser(data);
          }
        }
        await notificationsStore.fetchNotifications();
      } catch (error) {
        console.error('Error refreshing data:', error);
      }
    };

    const handleAppPause = () => {
      console.log('App paused');
    };

    const handleAppResume = async () => {
      console.log('App resumed, refreshing data...');
      await refreshData();
      
      // Check if we're on the login page or a public route
      const currentRoute = router.currentRoute.value;
      if (!currentRoute.meta.public && currentRoute.path === '/') {
        // Only redirect to tab1 if we're on the root path and not a public route
        router.replace('/tabs/tab1');
      }
    };

    const initializeApp = async () => {
      console.log('App mounted, initializing...');
      
   
      // Set up app state listeners
      App.addListener('appStateChange', ({ isActive }: AppStateChange) => {
        if (isActive) {
          // Handle resume asynchronously
          handleAppResume().catch(error => {
            console.error('Error handling app resume:', error);
          });
        } else {
          handleAppPause();
        }
      });
      
      // Initialize realtime service if user has a rooftop_id
      const currentUser = userStore.user.value;
      
      if (currentUser?.rooftop_id) {
        await realtimeService.initialize(currentUser.rooftop_id);
      }
    };

    async function checkAuth() {
      try {
        const { data: { session } } = await supabase.auth.getSession()
        
        // If we should stay on the reset password page, don't redirect
        if (authMiddleware.shouldStayOnResetPage()) {
          console.log('🔒 Staying on reset password page due to active token');
          return;
        }
        
        // ... existing code ...
      } catch (error) {
        console.error('Auth check error:', error)
      }
    }

    onMounted(async () => {
      try {
        console.log('App mounted, initializing...')
        
        // Check if we should stay on the reset password page
        if (authMiddleware.shouldStayOnResetPage()) {
          console.log('🔒 Skipping default navigation due to active reset flow');
          return;
        }
        
        // Check for password reset tokens immediately, before any other operations
        checkForPasswordReset();
        
        // Force light mode immediately
        forceLightMode();
        
        // Fix toggle buttons
        fixToggleButtons();
        
        // Get the current session
        const { data: { session } } = await supabase.auth.getSession();
        
      
        getAppInfo();

        if (session?.user) {
          console.log('Found existing session, setting up user...');
          await setupUser(session.user.id);
        } else {
          console.log('No active session found');
        }
        
        // Initialize app after user setup
        await initializeApp();
      } catch (error) {
        console.error('App mount error:', error)
      }
    });

    // Clean up on unmount
    onUnmounted(() => {
      console.log('App unmounting, cleaning up subscriptions...');
      if (notificationSubscription) {
        notificationSubscription.unsubscribe();
      }
    });

    // Create a named function for the focus event
    const handleWindowFocus = () => {
      // Check if we're on a route with disabled auto-refresh
      const currentRoute = router.currentRoute.value;
    };

   

    // Check for password reset links immediately
    const checkForPasswordReset = () => {
      // Log current location details for debugging
      console.log('🔍 App startup check - Path:', window.location.pathname, 'Hash:', window.location.hash);
      
      // Use the auth middleware
      if (authMiddleware.isInPasswordResetFlow()) {
        console.log('📋 App detects we are in password reset flow');
        authMiddleware.ensurePasswordResetPage();
      }
    };

    const setupUser = async (userId: string) => {
      try {
        // Get the last route from localStorage
        const lastRoute = localStorage.getItem('lastRoute');
        
        // Check for saved state first
        const { data: profile, error: profileError } = await supabase
          .from('profiles')
          .select('id, username, is_admin, is_super_admin, is_owner, rooftop_id')
          .eq('id', userId)
          .single();

        if (profileError) throw profileError;

        // Set user in store
        userStore.setUser({
          id: profile.id,
          username: profile.username,
          isAdmin: profile.is_admin,
          isSuperAdmin: profile.is_super_admin || false,
          isOwner: profile.is_owner || false,
          rooftop_id: profile.rooftop_id,
          full_name: '',
          avatar_url: '',
          updated_at: ''
        });

        // Setup notifications after user is set up
        if (profile.rooftop_id) {
          await setupNotifications();
          
          // If we're already on the dashboard, don't redirect
          if (router.currentRoute.value.path === '/admin/dashboard' || 
              router.currentRoute.value.path.startsWith('/tabs/')) {
            console.log('Already on dashboard or tabs - skipping route restoration');
            return;
          }
          
          // Route restoration logic
          if (lastRoute) {
            try {
              const savedRoute = JSON.parse(lastRoute);
              console.log('Restoring route:', savedRoute);
              router.replace(savedRoute.path);
            } catch (error) {
              console.error('Error restoring route:', error);
              router.replace('/tabs/tab1');
            }
          } else {
            // Only redirect to tab1 if no saved route
            router.replace('/tabs/tab1');
          }
        } else {
          console.error('No rooftop ID found for user');
        }
      } catch (error) {
        console.error('Error in setupUser:', error);
      }
    };
    
    const setupNotifications = async () => {
      console.log('Setting up notifications...');
      await notificationsStore.fetchNotifications();
      
      if (notificationSubscription) {
        console.log('Cleaning up old subscription...');
        notificationSubscription.unsubscribe();
      }
      
      console.log('Creating new subscription...');
      notificationSubscription = notificationsStore.setupNotificationsSubscription();
    };

    // Set up window focus handler for web using our named function
    window.addEventListener('focus', handleWindowFocus);

    // Track app state changes
    try {
      App.addListener('pause', () => {
        console.log('App paused - saving state');
      });

      App.addListener('resume', () => {
        console.log('App resumed - checking state');
      });
    } catch (error) {
      console.error('Error setting up app state listeners:', error);
    }

    return {}; 
  }
});



const getAppInfo = async () => {
  const appInfo = await appUpdateService.getAppInfo();
  console.log('MAIN: App info:', appInfo);
  const minimumVersion = await appUpdateService.getMinimumVersion();
  console.log('MAIN: Minimum version:', minimumVersion);
  if (appInfo.version < minimumVersion!) {
    const alert = await alertController.create({
        header: 'Update Available',
        message: 'A new version of Grip is required. Update now for the latest features and improvements.',
        backdropDismiss: false,
        buttons: [
          {
            text: 'Update',
            handler: () => {
              const platform = Capacitor.getPlatform();
              if (platform === 'ios') {
                window.open('itms-apps://itunes.apple.com/app/6739072259');
              } else {
                window.open('market://details?id=com.gripintelligence.app');
              }
            }
          }
        ]
      });
      await alert.present();
  }
  return appInfo;  
}
</script>

<style>
/* Basic Ionic styling */
:root {
  --ion-color-primary: #3880ff;
  --ion-color-secondary: #3dc2ff;
  --ion-color-tertiary: #5260ff;
  --ion-color-success: #2dd36f;
  --ion-color-warning: #ffc409;
  --ion-color-danger: #eb445a;
}

/* Dark theme defaults */
:root {
  --ion-color-scheme: dark;
  --ion-background-color: #1C1C1E;
  --ion-background-color-rgb: 28,28,30;
  --ion-text-color: #FFFFFF;
  --ion-text-color-rgb: 255,255,255;
  --ion-border-color: rgba(255,255,255,0.1);
}

/* Custom Alert Styles for Dark Mode */
.custom-alert.dark-mode-alert {
  --background: #1c1c1e !important;
}

.custom-alert.dark-mode-alert::part(title),
.custom-alert.dark-mode-alert::part(message),
.custom-alert.dark-mode-alert::part(sub-title) {
  color: #ffffff !important;
}

.custom-alert.dark-mode-alert .alert-title,
.custom-alert.dark-mode-alert .alert-message,
.custom-alert.dark-mode-alert .alert-sub-title {
  color: #ffffff !important;
}

.custom-alert.dark-mode-alert .alert-button {
  color: #ffffff !important;
}

.custom-alert.dark-mode-alert .alert-button-terms {
  color: #0a84ff !important;
}

.custom-alert.dark-mode-alert .alert-button-cancel {
  color: #8e8e93 !important;
}

.custom-alert.dark-mode-alert .alert-button-confirm {
  color: #0a84ff !important;
}

/* Light Mode Alert Styles */
@media (prefers-color-scheme: light) {
  .custom-alert.dark-mode-alert {
    --background: #ffffff !important;
  }

  .custom-alert.dark-mode-alert::part(title),
  .custom-alert.dark-mode-alert::part(message),
  .custom-alert.dark-mode-alert::part(sub-title) {
    color: #000000 !important;
  }

  .custom-alert.dark-mode-alert .alert-title,
  .custom-alert.dark-mode-alert .alert-message,
  .custom-alert.dark-mode-alert .alert-sub-title {
    color: #000000 !important;
  }

  .custom-alert.dark-mode-alert .alert-button {
    color: #000000 !important;
  }

  .custom-alert.dark-mode-alert .alert-button-terms {
    color: #007aff !important;
  }

  .custom-alert.dark-mode-alert .alert-button-cancel {
    color: #8e8e93 !important;
  }

  .custom-alert.dark-mode-alert .alert-button-confirm {
    color: #007aff !important;
  }
}

/* Dark theme styles */
ion-tab-bar {
  --background: #2C2C2E;
  border-top: 1px solid rgba(255,255,255,0.1);
  padding: 8px 0;
}

ion-tab-button {
  --color: #8E8E93;
  --color-selected: #FFFFFF;
  position: relative;
}

ion-toolbar {
  --background: #2C2C2E;
  --color: #FFFFFF;
  border-bottom: 1px solid rgba(255,255,255,0.1);
}

ion-header {
  --background: #2C2C2E;
}

ion-header.header-md::after {
  display: none;
}

/* Additional dark theme overrides */
ion-content {
  --background: #1C1C1E;
}

/* Select Modal Styles */
ion-select-option,
ion-action-sheet-button {
  --color: #FFFFFF !important;
}

ion-action-sheet {
  --background: #2C2C2E !important;
  --button-background: #2C2C2E !important;
  --button-background-selected: #3A3A3C !important;
}

ion-action-sheet-button.action-sheet-selected {
  color: #FFFFFF !important;
  font-weight: bold !important;
}

ion-action-sheet-button.action-sheet-cancel {
  color: #FFFFFF !important;
}

/* Light theme overrides */
@media (prefers-color-scheme: light) {
  ion-select-option,
  ion-action-sheet-button {
    --color: var(--ion-color-dark) !important;
  }

  ion-action-sheet {
    --background: var(--ion-color-light) !important;
    --button-background: var(--ion-color-light) !important;
    --button-background-selected: var(--ion-color-light-shade) !important;
  }

  ion-action-sheet-button.action-sheet-selected {
    color: var(--ion-color-dark) !important;
  }

  ion-action-sheet-button.action-sheet-cancel {
    color: var(--ion-color-dark) !important;
  }
}

ion-content::part(background) {
  background: #1C1C1E;
}

ion-modal ion-content {
  --background: #1C1C1E;
}

ion-item {
  --background: transparent;
  --color: #FFFFFF;
}

ion-card {
  --background: #2C2C2E;
  --border-radius: 16px;
  box-shadow: 0 4px 12px rgba(0,0,0,0.2);
}

ion-label {
  --color: #FFFFFF;
}

ion-input {
  --color: #FFFFFF;
  --background: #3A3A3C;
  --border-radius: 12px;
  --padding-start: 16px;
  --padding-end: 16px;
}

/* Calendar/Datetime Styles */
ion-datetime {
  --background: #2C2C2E;
  --ion-color-primary: #FFFFFF !important;
  --ion-color-primary-contrast: #2C2C2E !important;
  color: #FFFFFF;
}

ion-datetime::part(active) {
  color: #2C2C2E !important;
  background: #FFFFFF !important;
}

ion-datetime::part(calendar-day):focus {
  background: #FFFFFF !important;
  color: #2C2C2E !important;
}

ion-datetime::part(calendar-day selected) {
  background: #FFFFFF !important;
  color: #2C2C2E !important;
}

ion-datetime::part(calendar-day active) {
  background: #FFFFFF !important;
  color: #2C2C2E !important;
}

/* Light theme overrides for calendar */
@media (prefers-color-scheme: light) {
  ion-datetime {
    --background: #FFFFFF;
    --ion-color-primary: var(--ion-color-dark) !important;
    --ion-color-primary-contrast: #FFFFFF !important;
    color: var(--ion-color-dark);
  }

  ion-datetime::part(active) {
    color: #FFFFFF !important;
    background: var(--ion-color-dark) !important;
  }

  ion-datetime::part(calendar-day):focus {
    background: var(--ion-color-dark) !important;
    color: #FFFFFF !important;
  }

  ion-datetime::part(calendar-day selected) {
    background: var(--ion-color-dark) !important;
    color: #FFFFFF !important;
  }

  ion-datetime::part(calendar-day active) {
    background: var(--ion-color-dark) !important;
    color: #FFFFFF !important;
  }
}

/* Alert/Select Modal Styles for Dark Theme */
ion-alert {
  --background: #2C2C2E !important;
}

.alert-radio-group::-webkit-scrollbar {
  display: none;
}

.alert-radio-group {
  background: #2C2C2E !important;
}

.alert-radio-label {
  color: #FFFFFF !important;
}

.alert-radio-icon {
  border-color: #FFFFFF !important;
}

.alert-button-group {
  background: #2C2C2E !important;
}

.alert-button {
  color: #FFFFFF !important;
}

.alert-radio-inner {
  background-color: #FFFFFF !important;
}

.alert-wrapper {
  background: #2C2C2E !important;
}

.alert-radio-group button {
  background: #2C2C2E !important;
  color: #FFFFFF !important;
}

.alert-radio-group .alert-radio-button.alert-radio-button-checked {
  background: #3A3A3C !important;
}

/* Add styles for radio button highlight */
.alert-radio-button[aria-checked="true"] .alert-radio-inner {
  border-color: #FFFFFF !important;
}

.alert-radio-button[aria-checked="true"] {
  border-color: #FFFFFF !important;
}

.alert-radio-button::part(native) {
  --highlight-color: #FFFFFF !important;
}

.alert-radio-button[aria-checked="true"]::part(container) {
  border-color: #FFFFFF !important;
}

.alert-radio-button[aria-checked="true"] .alert-button-inner {
  border-color: #FFFFFF !important;
}

.alert-radio-button.alert-radio-button-checked::before {
  border-color: #FFFFFF !important;
}

/* Light theme overrides */
@media (prefers-color-scheme: light) {
  ion-alert {
    --background: #FFFFFF !important;
  }

  .alert-radio-group {
    background: #FFFFFF !important;
  }

  .alert-radio-label {
    color: var(--ion-color-dark) !important;
  }

  .alert-radio-icon {
    border-color: var(--ion-color-dark) !important;
  }

  .alert-button-group {
    background: #FFFFFF !important;
  }

  .alert-button {
    color: var(--ion-color-dark) !important;
  }

  .alert-radio-inner {
    background-color: var(--ion-color-dark) !important;
  }

  .alert-wrapper {
    background: #FFFFFF !important;
  }

  .alert-radio-group button {
    background: #FFFFFF !important;
    color: var(--ion-color-dark) !important;
  }

  .alert-radio-group .alert-radio-button.alert-radio-button-checked {
    background: var(--ion-color-light-shade) !important;
  }

  /* Add styles for radio button highlight */
  .alert-radio-button[aria-checked="true"] .alert-radio-inner {
    border-color: var(--ion-color-dark) !important;
  }

  .alert-radio-button[aria-checked="true"] {
    border-color: var(--ion-color-dark) !important;
  }

  .alert-radio-button::part(native) {
    --highlight-color: var(--ion-color-dark) !important;
  }

  .alert-radio-button[aria-checked="true"]::part(container) {
    border-color: var(--ion-color-dark) !important;
  }

  .alert-radio-button[aria-checked="true"] .alert-button-inner {
    border-color: var(--ion-color-dark) !important;
  }

  .alert-radio-button.alert-radio-button-checked::before {
    border-color: var(--ion-color-dark) !important;
  }
}

/* Toast notification styles for dark mode */
ion-toast {
  --background: #2C2C2E !important;
  --color: #FFFFFF !important;
  --button-color: #FFFFFF !important;
  --border-color: rgba(255, 255, 255, 0.1) !important;
  --border-radius: 8px !important;
  --box-shadow: 0 4px 16px rgba(0, 0, 0, 0.4) !important;
}

/* Stronger selectors for primary toast */
ion-toast.toast-primary,
ion-toast.premium-toast.toast-primary,
ion-toast[color="primary"] {
  --background: #838383 !important;
  --color: #FFFFFF !important;
  --button-color: #FFFFFF !important;
}

ion-toast.toast-success {
  --background: #2dd36f !important;
  --color: #FFFFFF !important;
}

ion-toast.toast-danger {
  --background: #eb445a !important;
  --color: #FFFFFF !important;
}

ion-toast.toast-warning {
  --background: #ffc409 !important;
  --color: #000000 !important;
}

ion-toast.toast-primary {
  --background: #838383 !important;
  --color: #FFFFFF !important;
}

ion-toast.toast-info {
  --background: #838383 !important;
  --color: #FFFFFF !important;
}

/* Custom gray toast - more specific selectors */
ion-toast.custom-gray-toast {
  --background: #838383 !important;
  --color: #FFFFFF !important;
}

ion-toast.custom-gray-toast::part(wrapper) {
  background-color: #838383 !important;
  color: #FFFFFF !important;
}

ion-toast.custom-gray-toast::part(message) {
  color: #FFFFFF !important;
}

/* Modal styles for light mode */
@media (prefers-color-scheme: light) {
  /* Override the default dark mode styles for modals in light mode */
  ion-modal {
    --backdrop-opacity: 0.6 !important;
    --ion-text-color: #333333 !important;
    --ion-background-color: #ffffff !important;
  }
  
  ion-modal ion-content {
    --background: #ffffff !important;
    background: #ffffff !important;
  }
  
  ion-modal ion-header ion-toolbar {
    --background: #ffffff !important;
    --color: #333333 !important;
    --border-color: #e0e0e0 !important;
  }
  
  ion-modal ion-title {
    color: #333333 !important;
  }
  
  ion-modal ion-buttons ion-button {
    --color: #333333 !important;
  }
  
  ion-modal ion-item {
    --background: #ffffff !important;
    --color: #333333 !important;
    --border-color: #e0e0e0 !important;
  }
  
  ion-modal ion-label {
    --color: #333333 !important;
    color: #333333 !important;
  }
  
  ion-modal ion-input {
    --color: #333333 !important;
    --background: transparent !important;
    --border-color: #e0e0e0 !important;
    --border-style: solid !important;
    --border-width: 0 0 1px 0 !important;
    --border-radius: 0 !important;
    --padding-start: 0 !important;
    --padding-end: 0 !important;
  }
  
  ion-modal ion-note {
    color: #666666 !important;
  }
  
  ion-modal h2, 
  ion-modal h3, 
  ion-modal h4,
  ion-modal p,
  ion-modal strong {
    color: #333333 !important;
  }
  
  ion-modal ion-list {
    background: transparent !important;
  }
  
  ion-modal ion-list-header {
    color: #333333 !important;
  }
  
  /* Target the modal page directly */
  ion-modal ion-page {
    background: #ffffff !important;
  }
  
  /* Target the modal content directly */
  ion-modal .ion-page {
    background: #ffffff !important;
  }
  
  /* Target the modal delegate host */
  ion-modal ion-delegate-host ion-page {
    background: #ffffff !important;
  }
  
  /* Target specific parts of the modal */
  ion-modal::part(content) {
    background: #ffffff !important;
  }
  
  ion-modal::part(handle) {
    background: #cccccc !important;
  }
  
  /* Specific styles for Edit User modal */
  ion-modal[aria-label="Edit User"] ion-content,
  ion-modal[aria-labelledby="Edit User"] ion-content,
  ion-modal:has(ion-title:contains("Edit User")) ion-content {
    --background: #ffffff !important;
    background: #ffffff !important;
  }
  
  ion-modal[aria-label="Edit User"] ion-item,
  ion-modal[aria-labelledby="Edit User"] ion-item,
  ion-modal:has(ion-title:contains("Edit User")) ion-item {
    --background: transparent !important;
    background: transparent !important;
    --inner-padding-end: 0 !important;
    --inner-padding-start: 0 !important;
    --padding-start: 0 !important;
    --padding-end: 0 !important;
    --border-color: transparent !important;
    --highlight-color-focused: transparent !important;
    box-shadow: none !important;
  }
  
  ion-modal[aria-label="Edit User"] ion-label,
  ion-modal[aria-labelledby="Edit User"] ion-label,
  ion-modal:has(ion-title:contains("Edit User")) ion-label {
    --color: #333333 !important;
    color: #333333 !important;
  }
  
  ion-modal[aria-label="Edit User"] ion-input,
  ion-modal[aria-labelledby="Edit User"] ion-input,
  ion-modal:has(ion-title:contains("Edit User")) ion-input {
    --color: #333333 !important;
    --background: transparent !important;
    background: transparent !important;
    --border-color: #e0e0e0 !important;
    --border-style: solid !important;
    --border-width: 0 0 1px 0 !important;
    --border-radius: 0 !important;
    --padding-start: 0 !important;
    --padding-end: 0 !important;
    margin-top: 8px !important;
    margin-bottom: 8px !important;
    box-shadow: none !important;
  }
  
  /* Remove background from all inputs in Edit User modal */
  ion-modal[aria-label="Edit User"] ion-item ion-input,
  ion-modal[aria-labelledby="Edit User"] ion-item ion-input,
  ion-modal:has(ion-title:contains("Edit User")) ion-item ion-input,
  ion-modal:has(ion-title:contains("Edit User")) ion-item.item-input {
    --background: transparent !important;
    background: transparent !important;
    box-shadow: none !important;
  }
  
  /* Specifically target the gray background in Edit User modal */
  ion-modal ion-item.item-has-value,
  ion-modal ion-item.item-has-focus,
  ion-modal ion-item.item-interactive,
  ion-modal ion-item.item-input,
  ion-modal ion-item.item-label,
  ion-modal ion-item.item {
    --background: transparent !important;
    background: transparent !important;
    --background-hover: transparent !important;
    --background-focused: transparent !important;
    --background-activated: transparent !important;
    --ripple-color: transparent !important;
    box-shadow: none !important;
  }
  
  /* Target the native input element */
  ion-modal ion-input::part(native),
  ion-modal ion-input::shadow(native),
  ion-modal ion-input .native-input,
  ion-modal ion-input input {
    background: transparent !important;
  }
  
  /* Target the input container */
  ion-modal ion-input,
  ion-modal .input-wrapper,
  ion-modal .input-container {
    --background: transparent !important;
    background: transparent !important;
  }
  
  /* Target the item wrapper */
  ion-modal ion-item::part(native),
  ion-modal ion-item::shadow(native),
  ion-modal ion-item .item-native {
    background: transparent !important;
  }
  
  /* Force all text in modals to be visible in light mode */
  ion-modal * {
    color: #333333 !important;
  }
  
  ion-modal ion-item * {
    --color: #333333 !important;
    color: #333333 !important;
  }
  
  ion-modal ion-input::part(native) {
    color: #333333 !important;
  }
  
  ion-modal ion-toggle {
    --background: #d7d7d7 !important;
    --background-checked: #838383 !important;
    --handle-background: #ffffff !important;
    --handle-background-checked: #ffffff !important;
  }
  
  /* Super aggressive styling for all modals in light mode */
  ion-modal::part(content) {
    --ion-text-color: #333333 !important;
    --ion-background-color: #ffffff !important;
    background-color: #ffffff !important;
  }
  
  ion-modal ion-content::part(scroll) {
    --ion-text-color: #333333 !important;
    --ion-background-color: #ffffff !important;
    background-color: #ffffff !important;
  }
  
  /* Target the Edit User modal specifically */
  ion-modal:has(ion-title) ion-content,
  ion-modal:has(form) ion-content {
    --background: #ffffff !important;
    background: #ffffff !important;
  }
  
  /* Target all text elements in modals */
  ion-modal h1, ion-modal h2, ion-modal h3, ion-modal h4, ion-modal h5, ion-modal h6,
  ion-modal p, ion-modal span, ion-modal div, ion-modal strong, ion-modal em,
  ion-modal ion-label, ion-modal ion-text, ion-modal ion-note, ion-modal ion-title {
    color: #333333 !important;
  }
}

/* Modal styles for dark mode */
@media (prefers-color-scheme: dark) {
  ion-modal {
    --backdrop-opacity: 0.6 !important;
    --ion-text-color: #ffffff !important;
    --ion-background-color: #1c1c1e !important;
  }
  
  ion-modal ion-content {
    --background: #1c1c1e !important;
    background: #1c1c1e !important;
  }
  
  ion-modal ion-header ion-toolbar {
    --background: #1c1c1e !important;
    --color: #ffffff !important;
    --border-color: rgba(255, 255, 255, 0.1) !important;
  }
  
  ion-modal ion-title {
    color: #ffffff !important;
  }
  
  ion-modal ion-buttons ion-button {
    --color: #ffffff !important;
  }
  
  ion-modal ion-item {
    --background: #1c1c1e !important;
    --color: #ffffff !important;
    --border-color: rgba(255, 255, 255, 0.1) !important;
  }
  
  ion-modal ion-label {
    --color: #ffffff !important;
    color: #ffffff !important;
  }
  
  ion-modal ion-input {
    --color: #ffffff !important;
    --background: #2c2c2e !important;
  }
  
  ion-modal ion-note {
    color: #8e8e93 !important;
  }
  
  ion-modal h2, 
  ion-modal h3, 
  ion-modal h4,
  ion-modal p,
  ion-modal strong {
    color: #ffffff !important;
  }
  
  ion-modal ion-list {
    background: transparent !important;
  }
  
  ion-modal ion-list-header {
    color: #ffffff !important;
  }
  
  /* Target the modal page directly */
  ion-modal ion-page {
    background: #1c1c1e !important;
  }
  
  /* Target the modal content directly */
  ion-modal .ion-page {
    background: #1c1c1e !important;
  }
  
  /* Target the modal delegate host */
  ion-modal ion-delegate-host ion-page {
    background: #1c1c1e !important;
  }
  
  /* Target specific parts of the modal */
  ion-modal::part(content) {
    background: #1c1c1e !important;
  }
  
  ion-modal::part(handle) {
    background: #3a3a3c !important;
  }
  
  /* This will override the global styles with higher specificity */
  body:not(.light-mode) ion-modal * {
    color: #ffffff !important;
  }
  
  body:not(.light-mode) ion-modal ion-item * {
    --color: #ffffff !important;
    color: #ffffff !important;
  }
  
  body:not(.light-mode) ion-modal ion-input::part(native) {
    color: #ffffff !important;
  }
  
  body:not(.light-mode) ion-modal ion-toggle {
    --background: #3a3a3c !important;
    --background-checked: #ffffff !important;
    --handle-background: #8e8e93 !important;
    --handle-background-checked: #1c1c1e !important;
  }
  
  /* Super aggressive styling for all modals in dark mode */
  body:not(.light-mode) ion-modal {
    --ion-text-color: #ffffff !important;
    --ion-background-color: #1c1c1e !important;
  }
  
  body:not(.light-mode) ion-modal::part(content) {
    --ion-text-color: #ffffff !important;
    --ion-background-color: #1c1c1e !important;
    background-color: #1c1c1e !important;
  }
  
  body:not(.light-mode) ion-modal ion-content::part(scroll) {
    --ion-text-color: #ffffff !important;
    --ion-background-color: #1c1c1e !important;
    background-color: #1c1c1e !important;
  }
  
  /* Target all text elements in modals with higher specificity for dark mode */
  body:not(.light-mode) ion-modal h1, 
  body:not(.light-mode) ion-modal h2, 
  body:not(.light-mode) ion-modal h3, 
  body:not(.light-mode) ion-modal h4, 
  body:not(.light-mode) ion-modal h5, 
  body:not(.light-mode) ion-modal h6,
  body:not(.light-mode) ion-modal p, 
  body:not(.light-mode) ion-modal span, 
  body:not(.light-mode) ion-modal div, 
  body:not(.light-mode) ion-modal strong, 
  body:not(.light-mode) ion-modal em,
  body:not(.light-mode) ion-modal ion-label, 
  body:not(.light-mode) ion-modal ion-text, 
  body:not(.light-mode) ion-modal ion-note, 
  body:not(.light-mode) ion-modal ion-title {
    color: #ffffff !important;
  }
}

/* Global styles to remove gray backgrounds from inputs */
ion-modal ion-item.ion-untouched,
ion-modal ion-item.item-interactive,
ion-modal ion-item.item-input,
ion-modal ion-item.item-fill-none,
ion-modal ion-item.item-fill-outline,
ion-modal ion-item.item-fill-solid {
  --background: transparent !important;
  background: transparent !important;
  --background-hover: transparent !important;
  --background-focused: transparent !important;
  --background-activated: transparent !important;
  --ripple-color: transparent !important;
  box-shadow: none !important;
}

/* Force all text in modals to be visible */
ion-modal * {
  color: #333333 !important;
}

ion-modal ion-item * {
  --color: #333333 !important;
  color: #333333 !important;
}

ion-modal ion-input::part(native) {
  color: #333333 !important;
}

ion-modal ion-toggle {
  --background: #d7d7d7 !important;
  --background-checked: #838383 !important;
  --handle-background: #ffffff !important;
  --handle-background-checked: #ffffff !important;
}

/* Super aggressive styling for all modals */
ion-modal {
  --ion-text-color: #333333 !important;
  --ion-background-color: #ffffff !important;
}

ion-modal::part(content) {
  --ion-text-color: #333333 !important;
  --ion-background-color: #ffffff !important;
  background-color: #ffffff !important;
}

ion-modal ion-content::part(scroll) {
  --ion-text-color: #333333 !important;
  --ion-background-color: #ffffff !important;
  background-color: #ffffff !important;
}

/* Target the Edit User modal specifically */
ion-modal:has(ion-title) ion-content,
ion-modal:has(form) ion-content {
  --background: #ffffff !important;
  background: #ffffff !important;
}

/* Extremely specific selectors for Edit User modal inputs */
ion-modal[aria-label="Edit User"] ion-content ion-list ion-item,
ion-modal[aria-labelledby="Edit User"] ion-content ion-list ion-item,
ion-modal:has(ion-title:contains("Edit User")) ion-content ion-list ion-item,
ion-modal:has(ion-title:contains("Edit User")) ion-content form ion-item {
  --background: transparent !important;
  background: transparent !important;
  --background-hover: transparent !important;
  --background-focused: transparent !important;
  --background-activated: transparent !important;
  --ripple-color: transparent !important;
  box-shadow: none !important;
  border-radius: 0 !important;
}

/* Target the input fields directly */
ion-modal[aria-label="Edit User"] ion-content ion-list ion-item ion-input,
ion-modal[aria-labelledby="Edit User"] ion-content ion-list ion-item ion-input,
ion-modal:has(ion-title:contains("Edit User")) ion-content ion-list ion-item ion-input,
ion-modal:has(ion-title:contains("Edit User")) ion-content form ion-item ion-input {
  --background: transparent !important;
  background: transparent !important;
  box-shadow: none !important;
  border-radius: 0 !important;
}
/* Global styles to remove gray backgrounds from inputs */
ion-input, ion-textarea {
  --background: transparent !important;
  background: transparent !important;
}

html, body, #app, ion-app, ion-content {
  background: #F5F5F7 !important;
  min-height: 100vh;
  height: 100%;
}

ion-content::part(background) {
  background: #F5F5F7 !important;
}

ion-app {
  min-height: 100vh;
  height: 100%;
  background: #F5F5F7 !important;
}

/* Force light mode styles that override dark mode completely */
.force-light-mode {
  color-scheme: light !important;
  --ion-color-scheme: light !important;
  --ion-background-color: #ffffff !important;
  --ion-background-color-rgb: 255,255,255 !important;
  --ion-text-color: #000000 !important;
  --ion-text-color-rgb: 0,0,0 !important;
  --ion-border-color: rgba(0,0,0,0.1) !important;
}

/* Override any dark mode styles when force-light-mode class is present */
.force-light-mode ion-content {
  --background: #F5F5F7 !important;
  background: #F5F5F7 !important;
}

.force-light-mode ion-modal {
  --backdrop-opacity: 0.6 !important;
  --ion-text-color: #333333 !important;
  --ion-background-color: #ffffff !important;
}

.force-light-mode ion-modal ion-content {
  --background: #ffffff !important;
  background: #ffffff !important;
}

.force-light-mode ion-modal ion-header ion-toolbar {
  --background: #ffffff !important;
  --color: #333333 !important;
  --border-color: #e0e0e0 !important;
}

.force-light-mode ion-modal ion-title {
  color: #333333 !important;
}

.force-light-mode ion-modal ion-buttons ion-button {
  --color: #333333 !important;
}

.force-light-mode ion-modal ion-item {
  --background: #ffffff !important;
  --color: #333333 !important;
  --border-color: #e0e0e0 !important;
}

.force-light-mode ion-modal ion-label {
  --color: #333333 !important;
  color: #333333 !important;
}

.force-light-mode ion-modal ion-input {
  --color: #333333 !important;
  --background: #f5f5f5 !important;
}

.force-light-mode ion-modal ion-note {
  color: #666666 !important;
}

.force-light-mode ion-modal h2, 
.force-light-mode ion-modal h3, 
.force-light-mode ion-modal h4,
.force-light-mode ion-modal p,
.force-light-mode ion-modal strong {
  color: #333333 !important;
}

.force-light-mode ion-modal ion-list {
  background: transparent !important;
}

.force-light-mode ion-modal ion-list-header {
  color: #333333 !important;
}

.force-light-mode ion-modal ion-toggle {
  --background: #d7d7d7 !important;
  --background-checked: #838383 !important;
  --handle-background: #ffffff !important;
  --handle-background-checked: #ffffff !important;
  opacity: 1 !important;
  visibility: visible !important;
}

/* This ensures that dark mode media query doesn't override our forced light mode */
@media (prefers-color-scheme: dark) {
  .force-light-mode {
    color-scheme: light !important;
    --ion-color-scheme: light !important;
  }
  
  /* These selectors ensure dark mode is completely overridden */
  .force-light-mode ion-content,
  .force-light-mode ion-item,
  .force-light-mode ion-modal,
  .force-light-mode ion-modal ion-content,
  .force-light-mode ion-modal ion-header,
  .force-light-mode ion-modal ion-item,
  .force-light-mode ion-modal ion-list {
    color-scheme: light !important;
  }
  
  .force-light-mode ion-modal * {
    color: #333333 !important;
  }
  
  .force-light-mode ion-modal ion-toggle {
    --background: #d7d7d7 !important;
    --background-checked: #838383 !important;
    --handle-background: #ffffff !important;
    --handle-background-checked: #ffffff !important;
  }
}

/* Specific toggle button fixes - these target Shadow DOM parts */
.force-light-mode ion-toggle::part(track) {
  background: var(--background, #d7d7d7) !important;
  opacity: 1 !important;
  visibility: visible !important;
}

.force-light-mode ion-toggle[aria-checked="true"]::part(track) {
  background: var(--background-checked, #838383) !important; 
}

.force-light-mode ion-toggle::part(handle) {
  background: var(--handle-background, #ffffff) !important;
  opacity: 1 !important;
  visibility: visible !important;
  box-shadow: 0 3px 6px rgba(0, 0, 0, 0.2) !important;
}

.force-light-mode ion-toggle[aria-checked="true"]::part(handle) {
  background: var(--handle-background-checked, #ffffff) !important;
}

/* Force light mode for customer select alert */
.force-light-alert .alert-wrapper,
.force-light-alert .alert-head,
.force-light-alert .alert-message,
.force-light-alert .alert-button {
  background: #fff !important;
  color: #111 !important;
}

.force-light-alert .alert-button {
  color: #111 !important;
  background: #fff !important;
  border: none !important;
  font-weight: 600;
}

.force-light-alert .alert-button-group {
  background: #fff !important;
  border-top: 1px solid #fff !important;
}

/* Global styles for rooftop switcher dropdown text visibility */
ion-popover {
  --color: #000000 !important;
  --ion-text-color: #000000 !important;
}

ion-popover ion-content {
  --color: #000000 !important;
  --ion-text-color: #000000 !important;
}

ion-popover ion-item {
  --color: #000000 !important;
  color: #000000 !important;
  --ion-text-color: #000000 !important;
}

ion-popover ion-label {
  color: #000000 !important;
  --color: #000000 !important;
  --ion-text-color: #000000 !important;
}

ion-popover ion-radio {
  --color: #000000 !important;
  --color-checked: #3880ff !important;
}

ion-popover ion-list {
  --color: #000000 !important;
  --ion-text-color: #000000 !important;
}

/* Force all popover text to be black */
ion-popover * {
  color: #000000 !important;
  --ion-color-base: #000000 !important;
  --ion-text-color: #000000 !important;
}

/* Target specific Ionic classes for select options */
.select-interface-option,
.alert-radio-label,
.popover-content ion-item,
.popover-content ion-label,
.select-option-inner,
.item-inner,
.item-native,
.label-text-wrapper {
  color: #000000 !important;
  --color: #000000 !important;
  --ion-text-color: #000000 !important;
}
</style>
